<template>
  <div class="mobile-emulator">
    <div class="phone-frame">
      <div class="phone-header">
        <div class="status-bar">
          <div class="time">10:30</div>
          <div class="icons">
            <signal-outlined />
            <wifi-outlined />
            <battery-outlined />
          </div>
        </div>
        <div class="chat-header">
          <div class="back-button">
            <left-outlined />
          </div>
          <div class="chat-title">{{ chatTitle || '登腾官方公众号' }}</div>
          <div class="more-button">
            <more-outlined />
          </div>
        </div>
      </div>
      <div class="chat-body">
        <!-- 사용자 메시지가 있을 경우 표시 -->
        <div v-if="displayUserMessage" class="message-row user-message">
          <div class="message-bubble user-bubble">
            {{ displayUserMessage }}
          </div>
          <div class="avatar user-avatar">
            <user-outlined />
          </div>
        </div>
        
        <!-- 시스템 응답 메시지 -->
        <div class="message-row">
          <div class="avatar dentium-logo">
            <div class="logo-content">{{ logoText || '登腾' }}</div>
            <div class="logo-text">{{ logoSubText || 'Dentium' }}</div>
          </div>
          <div class="message-bubble system-bubble">
            <!-- SECURITY: v-html 미사용. data-miniprogram-appid 화이트리스트만 링크로 렌더링하고,
                 그 외 텍스트/미인식 태그는 {{ }} 바인딩으로 자동 이스케이프되어 리터럴 텍스트로만 표시된다. -->
            <template v-for="(seg, idx) in displayContentSegments" :key="idx">
              <span v-if="seg.type === 'link'" class="miniprogram-link">
                <link-outlined class="miniprogram-link__icon" />{{ seg.text }}
              </span>
              <template v-else>{{ seg.text }}</template>
            </template>
          </div>
        </div>
      </div>
      <div class="phone-footer">
        <div class="input-area">
          <div class="input-box">发送消息...</div>
          <div class="emoji-button">
            <smile-outlined />
          </div>
          <div class="add-button">
            <plus-outlined />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  SignalFilled,
  WifiOutlined,
  BatteryOutlined,
  LeftOutlined,
  EllipsisOutlined,
  UserOutlined,
  SmileOutlined,
  PlusOutlined,
  LinkOutlined
} from '@ant-design/icons-vue';
import { computed } from 'vue';
import { parseMessageSegments } from '../utils/miniprogramLink';

export default {
  name: 'MobileEmulator',
  components: {
    SignalOutlined: SignalFilled,
    WifiOutlined,
    BatteryOutlined,
    LeftOutlined,
    MoreOutlined: EllipsisOutlined,
    UserOutlined,
    SmileOutlined,
    PlusOutlined,
    LinkOutlined,
  },
  props: {
    content: {
      type: [String, Object],
      default: '',
    },
    userMessage: {
      type: String,
      default: '',
    },
    chatTitle: {
      type: String,
      default: '',
    },
    logoText: {
      type: String,
      default: '',
    },
    logoSubText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    // 객체 타입인 경우 response 필드 추출, 문자열인 경우 그대로 사용
    const displayContent = computed(() => {
      if (typeof props.content === 'object' && props.content !== null) {
        return props.content.response || '感谢您的关注。';
      }
      return props.content || '感谢您的关注。';
    });

    // 사용자 메시지 처리 개선 - userMessage prop 우선 사용
    const displayUserMessage = computed(() => {
      // 직접 userMessage prop이 있는 경우 우선 사용
      if (props.userMessage && props.userMessage.trim() !== '') {
        return props.userMessage;
      }
      // content가 객체이고 keyword 필드가 있는 경우
      if (typeof props.content === 'object' && props.content !== null && props.content.keyword) {
        return props.content.keyword;
      }
      return '';
    });

    // 위챗 스타일 미니앱 링크(<a data-miniprogram-appid=...>) 파싱 — v-html 미사용, 화이트리스트 세그먼트만 렌더링
    const displayContentSegments = computed(() => parseMessageSegments(displayContent.value));

    return {
      displayContent,
      displayContentSegments,
      displayUserMessage
    };
  }
};
</script>

<style lang="scss" scoped>
.mobile-emulator {
  width: 100%;
  max-width: 375px;
  border-radius: 30px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  background-color: #f7f7f7;
  border: 10px solid #e0e0e0;
  height: 600px;
  display: flex;
  flex-direction: column;
}

.phone-frame {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.phone-header {
  background-color: #f0f0f0;
}

.status-bar {
  display: flex;
  justify-content: space-between;
  padding: 5px 15px;
  background-color: #f8f8f8;
  font-size: 12px;
  
  .icons {
    display: flex;
    gap: 8px;
  }
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
  
  .chat-title {
    font-weight: 500;
    font-size: 16px;
    flex: 1;
    text-align: center;
  }
  
  .back-button, .more-button {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.message-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  max-width: 100%;

  &.user-message {
    flex-direction: row-reverse;
  }
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  margin-right: 10px;
  overflow: hidden;
  flex-shrink: 0;
  
  &.user-avatar {
    margin-right: 0;
    margin-left: 10px;
    background-color: #f0f0f0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.dentium-logo {
  background-color: #99C00C;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  .logo-content {
    font-size: 12px;
    font-weight: bold;
  }
  
  .logo-text {
    font-size: 8px;
  }
}

.message-bubble {
  max-width: 70%;
  padding: 10px;
  border-radius: 8px;
  word-break: break-word;
  white-space: pre-line;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &.system-bubble {
    background-color: white;
    
    &:before {
      content: "";
      position: absolute;
      left: -6px;
      top: 12px;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-right: 6px solid white;
    }
  }
  
  &.user-bubble {
    background-color: #a2e563;
    
    &:before {
      content: "";
      position: absolute;
      right: -6px;
      top: 12px;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-left: 6px solid #a2e563;
    }
  }
}

// 위챗 실제 렌더 스타일: 미니앱 딥링크 = 링크 블루(#576b95) + 작은 링크 아이콘, 인라인
.miniprogram-link {
  color: #576b95;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
}

.miniprogram-link__icon {
  font-size: 12px;
}

.phone-footer {
  padding: 10px;
  background-color: #f8f8f8;
  border-top: 1px solid #e0e0e0;
}

.input-area {
  display: flex;
  align-items: center;
  gap: 10px;
  
  .input-box {
    flex: 1;
    background-color: white;
    border-radius: 18px;
    padding: 8px 15px;
    font-size: 14px;
    color: #999;
  }
  
  .emoji-button, .add-button {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
